/**
 * Created by neo on 07.05.21.
 */
import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { TagCondition, createTagCondition } from '../SuperMacro/TagCondition';
import { GoogleTtsRequest, createGoogleTtsRequest } from './GoogleTtsRequest';
import { Audited, createAuditedFields } from '../../Audited';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Pageable } from '../../Interfaces/Pageable';
import { Media, createMedia } from '../../Media/Media';
import { TtsCacheResponse, createTtsCacheResponse } from './TtsCacheResponse';

const VARIABLES_REGEX = /(\{\{.*\}\})/gi;

export type CoachTtsGenerateRequest = {
  /**
   * Must be set when requesting multiple ones
   */
  identifier: string;
  tags?: string[];
  language: string;
  values?: Record<string, string>;
};

export type CoachTtsQueryRequest = Pageable & {
  identifier?: string;
  language?: string;
  tags?: string[];
};

export type CoachTts = Audited & {
  identifier: string;
  language: string;
  tags: string[];
  tagConditions: TagCondition[];
  googleTtsRequest: GoogleTtsRequest;
  readonly ssmlOrText: string | undefined;
  readonly variables: string[];
  save(): Promise<CoachTts>;
  delete(): Promise<CoachTts>;
  test(values?: Record<string, string>): Promise<string>;
};

export function createCoachTts(json?: Partial<CoachTts>): CoachTts {
  const audited = createAuditedFields(json);
  const tts: CoachTts = makeAutoObservable({
    ...audited,
    identifier: json?.identifier ?? '',
    language: json?.language ?? 'de',
    tags: json?.tags ?? [],
    tagConditions: ((json?.tagConditions as any) ?? []).map((t: any) => createTagCondition(t)),
    googleTtsRequest: createGoogleTtsRequest(json?.googleTtsRequest as any),

    get ssmlOrText(): string | undefined {
      return this.googleTtsRequest.input.ssml || this.googleTtsRequest.input.text;
    },

    get variables(): string[] {
      return (this.ssmlOrText || '').match(VARIABLES_REGEX) ?? [];
    },

    toJS(newId?: boolean) {
      const { id, createdAt, updatedAt, createdBy, updatedBy } = audited;
      return {
        id: newId ? UUID() : id,
        createdAt,
        updatedAt,
        createdBy,
        updatedBy,
        identifier: this.identifier,
        language: this.language,
        tags: this.tags,
        tagConditions: this.tagConditions.map((t) => t),
        googleTtsRequest: this.googleTtsRequest,
      };
    },

    save(): Promise<CoachTts> {
      return HttpBackend.post('/coach/text-to-speech/admin', this).then(() => this);
    },

    delete(): Promise<CoachTts> {
      return HttpBackend.delete(`/coach/text-to-speech/admin/${this.id}`).then(() => this);
    },

    test(values?: Record<string, string>): Promise<string> {
      return HttpBackend.post(`/coach/text-to-speech/admin/test`, Object.assign(this, { values }));
    },
  });

  return tts;
}

export const CoachTtsApi = {
  generate(requests: CoachTtsGenerateRequest[]): Promise<TtsCacheResponse[]> {
    return HttpBackend.post(`/coach/text-to-speech/generate`, requests).then((res) =>
      (res ?? []).map((r: any) => createTtsCacheResponse(r)),
    );
  },

  generateSingle(identifier: string, request: Partial<CoachTtsGenerateRequest>): Promise<Media | undefined> {
    return HttpBackend.post(`/coach/text-to-speech/${identifier}/generate`, request).then((res) =>
      res ? createMedia(res) : undefined,
    );
  },

  find(request?: Partial<CoachTtsQueryRequest>): Promise<CoachTts[]> {
    return HttpBackend.get(`/coach/text-to-speech/admin`, request).then((res) =>
      (res ?? []).map((t: any) => createCoachTts(t)),
    );
  },

  count(request: CoachTtsQueryRequest): Promise<number> {
    return HttpBackend.get(`/coach/text-to-speech/admin/count`, request).then((res) => res ?? 0);
  },

  findOne(id: string): Promise<CoachTts> {
    return HttpBackend.get(`/coach/text-to-speech/admin/${id}`).then((res) => createCoachTts(res));
  },
};
